
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, TrendingUp, Eye, Settings } from 'lucide-react';
import { useSelfLearningAnalytics } from '@/hooks/useSelfLearningAnalytics';

export const LearningInsights = () => {
  const { userPreferences, isLoading } = useSelfLearningAnalytics();
  const [showDetails, setShowDetails] = useState(false);

  if (!userPreferences) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Learning Analytics</h3>
        </div>
        <p className="text-muted-foreground">
          Start using the dashboard to help me learn your preferences and improve suggestions.
        </p>
      </Card>
    );
  }

  const confidenceLevel = userPreferences.confidenceScore >= 0.7 ? 'High' : 
                         userPreferences.confidenceScore >= 0.4 ? 'Medium' : 'Low';

  const confidenceColor = userPreferences.confidenceScore >= 0.7 ? 'bg-green-100 text-green-800' :
                          userPreferences.confidenceScore >= 0.4 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800';

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Learning Analytics</h3>
        </div>
        <Badge className={confidenceColor}>
          {confidenceLevel} Confidence
        </Badge>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Preferred Charts</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {userPreferences.preferredChartTypes.slice(0, 3).map(type => (
                <Badge key={type} variant="outline" className="text-xs">
                  {type}
                </Badge>
              ))}
              {userPreferences.preferredChartTypes.length === 0 && (
                <span className="text-xs text-muted-foreground">Learning...</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Focus Areas</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(userPreferences.dataColumnAffinities)
                .slice(0, 2)
                .map(([column, _]) => (
                  <Badge key={column} variant="outline" className="text-xs">
                    {column}
                  </Badge>
                ))}
              {Object.keys(userPreferences.dataColumnAffinities).length === 0 && (
                <span className="text-xs text-muted-foreground">Learning...</span>
              )}
            </div>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-3 border-t pt-4">
            <div>
              <h4 className="text-sm font-medium mb-2">KPI Preferences</h4>
              <div className="flex flex-wrap gap-1">
                {userPreferences.preferredKpiFormats.map(format => (
                  <Badge key={format} variant="secondary" className="text-xs">
                    {format}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2">Common Insights</h4>
              <div className="flex flex-wrap gap-1">
                {userPreferences.insightCategories.map(category => (
                  <Badge key={category} variant="secondary" className="text-xs">
                    {category}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground">
                Confidence Score: {(userPreferences.confidenceScore * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowDetails(!showDetails)}
          className="w-full"
        >
          <Settings className="h-4 w-4 mr-2" />
          {showDetails ? 'Hide Details' : 'Show Learning Details'}
        </Button>
      </div>
    </Card>
  );
};
